// Trade → sector map (spec 03 §1.4). It covers the 29 Industry values of the master file;
// admins may extend it before a run in scripts/import-leads.sectors.json.
import { foldText } from "./leadFields";

export type Sector = { value: string; fr: string; en: string };

const s = (value: string, fr: string, en: string): Sector => ({
  value,
  fr,
  en,
});

export const TRADE_SECTORS: Readonly<Record<string, Sector>> = Object.freeze({
  Electrician: s("electrician", "Électricien", "Electrician"),
  Plumber: s("plumber", "Plombier", "Plumber"),
  HVAC: s("hvac", "Chauffage / climatisation", "HVAC"),
  Landscaper: s("landscaper", "Paysagiste", "Landscaper"),
  Roofer: s("roofer", "Couvreur", "Roofer"),
  Painter: s("painter", "Peintre", "Painter"),
  "Auto Repair Shop": s(
    "auto_repair",
    "Garage de réparation automobile",
    "Auto repair shop",
  ),
  Flooring: s("flooring", "Revêtement de sol", "Flooring"),
  Carpenter: s("carpenter", "Charpentier-menuisier", "Carpenter"),
  "Tire Shop": s("tire_shop", "Atelier de pneus", "Tire shop"),
  "General Contractor": s(
    "general_contractor",
    "Entrepreneur général",
    "General contractor",
  ),
  "Cleaning Service": s("cleaning", "Entretien ménager", "Cleaning service"),
  "Pest Control": s("pest_control", "Extermination", "Pest control"),
  Locksmith: s("locksmith", "Serrurier", "Locksmith"),
  "Restoration & Remediation": s(
    "restoration",
    "Restauration après sinistre",
    "Restoration & remediation",
  ),
  "Junk Removal / Cleanout": s(
    "junk_removal",
    "Enlèvement de déchets",
    "Junk removal",
  ),
  "Snow Removal (Déneigement)": s(
    "snow_removal",
    "Déneigement",
    "Snow removal",
  ),
  Towing: s("towing", "Remorquage", "Towing"),
  "Garage Door Repair": s(
    "garage_door",
    "Portes de garage",
    "Garage door repair",
  ),
  "Glass & Window Repair": s(
    "glass_window",
    "Vitrerie",
    "Glass & window repair",
  ),
  "Moving Company": s("moving", "Déménagement", "Moving company"),
  "Fence Installation": s("fence", "Clôtures", "Fence installation"),
  Masonry: s("masonry", "Maçonnerie", "Masonry"),
  Excavation: s("excavation", "Excavation", "Excavation"),
  Insulation: s("insulation", "Isolation", "Insulation"),
  "Pool & Spa Services": s(
    "pool_spa",
    "Piscines et spas",
    "Pool & spa services",
  ),
  "Chimney Sweep": s("chimney", "Ramonage", "Chimney sweep"),
  "Appliance Repair": s(
    "appliance_repair",
    "Réparation d'électroménagers",
    "Appliance repair",
  ),
  Handyman: s("handyman", "Homme à tout faire", "Handyman"),
});

export const OTHER_SECTOR: Sector = Object.freeze(s("other", "Autre", "Other"));

export function sectorForTrade(
  trade: string | null | undefined,
  overrides: Readonly<Record<string, string>> = {},
): { sector: string; mapped: boolean } {
  if (!trade || !trade.trim())
    return { sector: OTHER_SECTOR.value, mapped: true };
  const key = foldText(trade);
  const override = Object.entries(overrides).find(([k]) => foldText(k) === key);
  if (override) return { sector: override[1], mapped: true };
  const hit = Object.entries(TRADE_SECTORS).find(([k]) => foldText(k) === key);
  return hit
    ? { sector: hit[1].value, mapped: true }
    : { sector: OTHER_SECTOR.value, mapped: false };
}

/** `companySectors` configuration (upstream shape), with "Autre" last. */
export function companySectorChoices(
  locale: "fr" | "en",
): { value: string; label: string }[] {
  return [...Object.values(TRADE_SECTORS), OTHER_SECTOR].map((sector) => ({
    value: sector.value,
    label: sector[locale],
  }));
}

/**
 * Display label of a raw companies.trade value (e.g. "Plumber" → « Plombier »); matching ignores case and accents.
 * Unknown trades show as stored. The raw value is what filters send (the queue compares exact values).
 */
export function tradeDisplayLabel(trade: string, locale: string): string {
  const key = foldText(trade);
  const hit = Object.entries(TRADE_SECTORS).find(([k]) => foldText(k) === key);
  return hit ? hit[1][locale === "en" ? "en" : "fr"] : trade;
}
